<script lang="ts" setup>
const { data: menu } = await useLazyFetch('/api/menu/footer', { server: false })
const { $t } = useNuxtApp()
const config = useRuntimeConfig()
const activeMenu = useState<string | null>('activeIndex', () => null)
const sections = {
    section1Title: $t('Produkty a služby'),
    section2Title: $t('Rýchle linky'),
    section3Title: $t('Dôležité odkazy'),
}
const { data, sendEvent } = useTracker()

const handleClick = ({ title, url }: { title: string; url: string }): void => {
    sendEvent({
        data: {
            ...data,
            title: title,
            click: url,
            position: 'Main Footer',
        },
    })
}

const currentYear = computed<number>((): number => new Date().getFullYear())
</script>
<template>
  <footer 
    v-if="menu"
    class="border-top border-secondary pt-8 pt-md-12 mb-12"
    aria-label="Päta stránky"
  >
    <Tracker :data="{ position: 'Main Footer' }">
      <ContainerFluid>
        <div class="row">
          <nav
            class="col-12 col-lg-8"
            :aria-label="`${$t('Navigácia:')} ${sections.section1Title}`"
          >
            <Tracker :data="{ title: sections.section1Title }">
              <FooterGroup :title="sections.section1Title">
                <FooterMenu
                  :categories="menu.data?.categories"
                />
              </FooterGroup>
            </Tracker>
          </nav>
          <div class="col-12 col-lg-4">
            <div class="row">
              <div class="col-12 col-md-3 col-lg-6 mb-4 mb-md-8">
                <section>
                  <Tracker
                    :data="{
                      title: sections.section2Title,
                    }"
                  >
                    <FooterGroup
                      :title="
                        sections.section2Title
                      "
                    />
                    <FooterLinks
                      :aria-label="sections.section2Title"
                      :links="menu.data.flash_links"
                    />
                  </Tracker>
                </section>
              </div>
              <div class="col-12 col-md-3 col-lg-6 mb-4 mb-md-8">
                <section>
                  <Tracker
                    :data="{
                      title: sections.section3Title,
                    }"
                  >
                    <FooterGroup
                      :title="
                        sections.section3Title
                      "
                    />
                    <FooterLinks
                      :aria-label="sections.section3Title"
                      :links="
                        menu.data.important_links
                      "
                    />
                  </Tracker>
                </section>
              </div>
              <div class="col-12 col-md-6 col-lg-12">
                <div class="text-md-end d-flex flex-column">
                  <div
                    class="d-flex justify-content-md-end order-3 order-md-1 mb-md-6"
                  >
                    <VNuxtLink
                      :to="
                        useNamedRoutes({ name: 'home' })
                      "
                      label="logo"
                    >
                      <img
                        :src="`${config.app.cdnURL}/${config.public.PUBLIC_IMAGES_URL}/logo.svg`"
                        width="159"
                        height="56"
                        alt="Kooperativa - Vienna insurance group"
                        class="d-block"
                        loading="lazy"
                        decoding="async"
                      >
                    </VNuxtLink>
                  </div>
                  <Typo class="order-2 mt-3 mt-md-0">
                    <p class="mb-3 mb-md-8">
                      <small class="fs-small d-block">
                        {{ $t('KOOPERATIVA poisťovňa, a.s.') }}
                        <span lang="en">Vienna Insurance Group</span>,
                        <br>
                        {{
                          $t(
                            'Mlynské nivy 16, 821 09 Bratislava, IČO: 00 585 441,'
                          )
                        }}
                        <br>
                        {{
                          $t(
                            'DIČ: 2020527300, IČ DPH: SK7020000746,'
                          )
                        }}
                        <br>
                        {{
                          $t(
                            'GIIN: EM4SY0.00023.ME.703'
                          )
                        }}
                      </small>
                    </p>
                    <p class="mb-8 mb-md-0">
                      <small class="fs-small">
                        <span lang="en">{{ $t('Copyright ©') }}</span> {{ currentYear }} {{ $t('KOOPERATIVA poisťovňa, a.s.') }}
                        <br>
                        <span lang="en">Vienna Insurance Group</span>
                        <br>
                        {{
                          $t(
                            'Všetky údaje a informácie uvedené na týchto webových stránkach majú informatívny charakter a nie sú právne záväzné.'
                          )
                        }}
                      </small>
                    </p>
                  </Typo>

                  <IconsWrapper>
                    <IconsSymbolAngleRight />
                    <IconsSymbolAngleLeft />
                    <IconsSymbolTimes />
                    <IconsSymbolInstagram />
                    <IconsSymbolFacebookF />
                    <IconsSymbolYoutube />
                    <IconsSymbolLinkedIn />
                    <IconsSymbolExternal />
                  </IconsWrapper>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="d-flex align-items-center gap-7 align-items-center justify-content-between pt-6 mt-5 mt-lg-0 mb-20 order-1 order-md-3 border-top flex-column flex-md-row"
        >
          <p class="fs-small text-center">
            {{ $t('Web portál vyvinutý spoločnosťou') }}
            <a
              href="https://www.scrtechnologies.sk/"
              target="_blank"
              class="fw-bold"
              :title="$t('SCR technologies - digitálne ekosystémy a team sourcing')"
              @click="
                handleClick({
                  title: 'SCR® technologies',
                  url: 'https://www.scrtechnologies.sk/',
                })
              "
            >
              {{ $t('SCR® technologies') }}
            </a>
          </p>
          <div class="d-flex gap-7 align-items-center">
            <SocialButtons
              :data="menu.data.socials.data"
              size="small"
            />
            <VNuxtLink
              :to="{ name: 'magazine' }"
              label="Magazín Icon"
              class="flex-shrink-0"
              :aria-label="$t('Magazín Kooperativa')"
            >
              <img
                src="~/assets/images/logo-magazine.svg"
                loading="lazy"
                decoding="async"
                width="23"
                height="21"
                class="d-block"
                alt=""
                aria-hidden="true"
              >
            </VNuxtLink>
          </div>
        </div>
      </ContainerFluid>
    </Tracker>
  </footer>
  <div
    class="menu-overlay position-fixed start-0 vh-100 w-100 z-index-10 top-0 bg-black transition"
    :class="activeMenu ? 'is-active' : ''"
  />
  <ClientOnly v-if="config.public.chat.ENABLED">
    <Tracker :data="{ position: 'Page', title: 'Chat' }">
      <LazyChatToggleButton />
    </Tracker>
  </ClientOnly>
</template>
